// bots-list.ts — pure helpers behind the All bots screen (Task 10, handoff README §4): each
// row's derived domain and source-rollup label, plus the plan-cap gate's visibility and copy.
// House pattern (`lib/usage.ts`, `lib/overview.ts`) — extracted so the branching a screenshot
// can't exercise (which plan to name in the upsell, when the gate disappears entirely) has real
// tests instead of only ever being eyeballed in the browser.
import { PLAN_LIMITS } from '@repo/shared-types';
import type { BotDto, BotListItemDto, SourceDto, SourceStatus } from '@repo/shared-types';
import type { StatusChipState } from '@repo/ui/components/status-chip';
import type { Catalog } from '../locales/en';

/** The bot's own site, derived from its sources — the ONE surviving definition (fix-wave item
 * 5). This used to be byte-identical in three places: here, `BotSwitcher.tsx`, and
 * `BotOverview.tsx`. `Bots.tsx` no longer calls this at all — the All-bots row's domain now
 * comes from `bot.health.domain`, computed server-side once for the whole collection instead
 * of a per-row sources fetch (fix-wave item 3, spec §7.2's N+1 guard). `BotSwitcher.tsx` and
 * `BotOverview.tsx` still fetch a single bot's own sources for other reasons (the identity
 * card, "Add sources"/"Re-sync") and derive the domain from that single-bot response with this
 * shared function, so all three screens can never disagree on the rule. `SourceType` has no
 * literal `website` value, so the first `url` source (as opposed to a sitemap, an uploaded
 * file, or pasted text) is the closest real signal. `null` when there isn't one — render
 * nothing rather than invent a placeholder. */
export function deriveDomain(sources: SourceDto[] | undefined): string | null {
  const site = sources?.find((s) => s.type === 'url');
  if (!site) return null;
  try {
    return new URL(site.uri).hostname;
  } catch {
    return null;
  }
}

/** `worstStatus`, folding in the one combination its own doc comment
 * (`BotOverviewDto['knowledge'].worstStatus`, `@repo/shared-types/dto.ts`) says every caller
 * must treat as "something is being removed": `worstStatus === 'ready' && !allReady`, reachable
 * ONLY via a `deleting` sibling (any other status outranks `ready` and would win `worstStatus`
 * outright instead — see that doc comment for the full reasoning). `lib/overview.ts`'s
 * `knowledgeChipView` applies this exact rule for the Overview screen's chip; this is the shared
 * version `sourceRollupLabel` below is now built on, and that the source-detail-diagnostics
 * final review routed `Bots.tsx`'s row chip and `BotSwitcher.tsx`'s popover chip/health-line
 * through too — all three used to re-derive this rule independently, and two of them missed it,
 * rendering a green "ready" chip on a bot whose only problem was a source being torn down. */
export function effectiveWorstStatus(
  health: Pick<BotListItemDto['health'], 'worstStatus' | 'allReady'>,
): SourceStatus | null {
  if (health.worstStatus === 'ready' && !health.allReady) return 'deleting';
  return health.worstStatus;
}

/** The row's SOURCES column (handoff README §4: `"3 · all ready"` / `"4 · 1 indexing"` /
 * `"1 · 1 failed"`). The per-status counts in those examples aren't reproducible from
 * `BotListItemDto['health']` — it carries one `worstStatus`, not a tally per state — so this
 * reports the honest aggregate instead of a fabricated count: `"N · all ready"` when every
 * source is ready, otherwise `"N · <worstStatus>"` verbatim. Zero sources is its own case
 * rather than a vacuous `"0 · all ready"`.
 *
 * Fix-wave item 4: this used to bucket `crawling` AND `indexing` into the single word
 * "indexing", so a mid-crawl bot showed "crawling" in the row's STATUS column (`StatusChip`,
 * driven straight off `worstStatus`) and "N · indexing" 200px away in this same row's SOURCES
 * column — two different words for one fact. Echoing `worstStatus` verbatim, rather than
 * mapping it through a second word list, means the two columns read off the same field and can
 * no longer contradict each other.
 *
 * Built on `effectiveWorstStatus` above (source-detail-diagnostics final review — previously this
 * had its own inline `worstStatus === 'ready'` check, duplicating the same rule that function now
 * owns): `worstStatus` is normally never `'ready'` here, since `allReady` above already caught
 * the case where every source is ready — reaching this line used to mean at least one source
 * outranks `ready` in `STATUS_SEVERITY`. `deleting` broke that: it ranks BELOW `ready`
 * (deliberately — a delete isn't a health problem), so a bot with one `ready` source and one
 * `deleting` source has `allReady: false` but `worstStatus: 'ready'`, and echoing that verbatim
 * would print "N · ready" on the row of a bot with something actively being removed.
 *
 * `t: Catalog` (locale-dashboard plan, Task 4, required) — the worst-status word is routed
 * through `t.status[...]`, the same catalog `StatusChip`'s own fallback reads (`en.ts`'s doc
 * comment on `status`), so this rollup and a chip elsewhere never disagree on the word for a
 * given state. `effectiveWorstStatus` can only read `null` here if `worstStatus` itself is
 * `null` while `sourceCount > 0` — a combination the health payload shouldn't produce (a
 * non-zero source count always carries a real worst status) and that isn't reachable through
 * any real caller today; kept as a defensive `String(null)` fallback (`"null"`, matching what
 * the old bare template-literal interpolation would have rendered) rather than a throw, so an
 * unexpected shape still degrades to *something* legible instead of crashing the row. */
export function sourceRollupLabel(
  health: Pick<BotListItemDto['health'], 'sourceCount' | 'worstStatus' | 'allReady'>,
  t: Catalog,
): string {
  if (health.sourceCount === 0) return t.bots.noSources;
  if (health.allReady) return t.bots.allSourcesReady({ n: health.sourceCount });
  const effective = effectiveWorstStatus(health);
  return t.bots.sourceStatusRollup({
    n: health.sourceCount,
    status: effective !== null ? t.status[effective] : String(effective),
  });
}

/** `Bots.tsx`'s row STATUS chip — moved here from that file (final review, source-detail-
 * diagnostics) alongside `botRowView` below purely so both component files stay component-only
 * exports (`react-refresh/only-export-components` — mixing a plain function export into a
 * component file breaks Fast Refresh for that file). Same status precedence
 * `BotSwitcher.tsx`'s `botRowView` uses: a paused bot reads as "paused" regardless of its
 * sources' own state, otherwise the worst source status stands in (`ready` when there isn't one
 * yet), routed through `effectiveWorstStatus` above so a bot with a `ready`+`deleting` mix reads
 * as `deleting`, not a false-positive `ready` — see that function's own doc comment. */
export function rowState(bot: Pick<BotDto, 'status'> & { health: BotListItemDto['health'] }): StatusChipState {
  return bot.status === 'paused' ? 'paused' : (effectiveWorstStatus(bot.health) ?? 'ready');
}

export interface BotRowView {
  state: StatusChipState;
  healthLine: string;
}

/** `BotSwitcher.tsx`'s popover-row chip state + mono health-line computation — moved here (final
 * review, source-detail-diagnostics) alongside `rowState` above, for the same Fast-Refresh
 * reason. `SourceStatus` values line up exactly with `StatusChipState`, so `worstStatus` maps
 * directly — no separate lookup table.
 *
 * `state` used to echo `health.worstStatus` verbatim, which reads 'ready' — not 'deleting' — for
 * a bot with one ready source and one deleting one ('deleting' ranks below 'ready' in
 * `STATUS_SEVERITY`, deliberately). That rendered a green "ready" chip AND fell through
 * `healthLine` to the plain message-count line, on the exact delete path any multi-source bot
 * takes. Routed through `effectiveWorstStatus` above (the same rule `rowState` and
 * `lib/overview.ts`'s `knowledgeChipView` both use), both the chip and the health line now agree
 * with `sourceRollupLabel`'s "N · deleting" SOURCES column instead of contradicting it.
 *
 * `t: Catalog` (locale-dashboard plan, Task 4, required) — every branch of `healthLine` moved to
 * a `t.bots.*` key; the blank-line placeholder stays a literal single space, not a catalog entry
 * — it's a layout stand-in (keeps the row's height stable pre-load), not copy. */
export function botRowView(
  bot: Pick<BotDto, 'status'>,
  health: BotListItemDto['health'] | undefined,
  t: Catalog,
): BotRowView {
  const effectiveStatus = health ? effectiveWorstStatus(health) : null;
  const state: StatusChipState = bot.status === 'paused' ? 'paused' : (effectiveStatus ?? 'ready');
  const healthLine = (() => {
    if (!health) return ' '; // health hasn't loaded yet — keep the row's height stable
    if (bot.status === 'paused') return t.bots.paused;
    if (effectiveStatus === 'deleting') return t.bots.sourcesRemoving({ n: health.sourceCount });
    if (health.worstStatus === 'error') return t.bots.sourcesSyncError({ n: health.sourceCount });
    if (health.worstStatus === 'crawling' || health.worstStatus === 'indexing') {
      return t.bots.sourcesSyncing({ n: health.sourceCount });
    }
    return t.bots.messagesUnanswered({ messages: health.messages14d, unanswered: health.unansweredGroups });
  })();
  return { state, healthLine };
}

export interface BotUpgradeOffer {
  name: string;
  priceUsd: number;
  maxBots: number;
}

/** The cheapest plan whose bot cap actually exceeds `cap` — deliberately NOT "one rung up the
 * price ladder" (that's `usage.ts`'s `nextPlan`, keyed by plan, not by cap): Free and Starter
 * both carry `maxBots: 1` in `PLAN_LIMITS`, so a Free workspace at its cap has to skip straight
 * to Growth, the only plan that actually raises the number — naming Starter would be a true
 * "upgrade" that does nothing for this specific limit. `null` when nothing clears it — the top
 * plan's own cap (Growth's 5, in the current catalog) has nothing above it left to name. */
export function cheapestPlanAbove(cap: number): BotUpgradeOffer | null {
  const [facts] = Object.values(PLAN_LIMITS)
    .filter((p) => p.maxBots > cap)
    .sort((a, b) => a.maxBots - b.maxBots);
  return facts ? { name: facts.name, priceUsd: facts.priceUsd, maxBots: facts.maxBots } : null;
}

export interface BotCapGate {
  /** `null` means uncapped (internal workspace) — callers must never gate on this. */
  cap: number | null;
  atCap: boolean;
  /** The dashed "Compare plans" box (handoff README §4). False at the top plan's own cap,
   * where the gate is replaced by the plain "N of N used" subtitle instead — see
   * `cheapestPlanAbove`'s doc comment for why "Compare plans" would be a dead link there. */
  showUpgradeGate: boolean;
  /** The plan named in the gate's copy. `null` whenever `showUpgradeGate` is false. */
  upgrade: BotUpgradeOffer | null;
}

export function botCapGate(botCount: number, cap: number | null): BotCapGate {
  const atCap = cap !== null && botCount >= cap;
  if (!atCap || cap === null) return { cap, atCap, showUpgradeGate: false, upgrade: null };
  const upgrade = cheapestPlanAbove(cap);
  return { cap, atCap, showUpgradeGate: upgrade !== null, upgrade };
}

/** The header subline (handoff README §4 / the shell mock's `botsUsedLabel`): "N of Cap used"
 * once a cap exists, otherwise the bare count for an uncapped (internal) workspace, which has
 * no "used of" fraction to report.
 *
 * `t: Catalog` (locale-dashboard plan, Task 4, required). */
export function botsUsedLabel(botCount: number, cap: number | null, t: Catalog): string {
  if (cap === null) return t.bots.botsCount({ n: botCount });
  return t.bots.botsOfCapUsed({ n: botCount, cap });
}
